import { Brand } from "@/components/Brand";
import { Download, FileText } from "lucide-react";

const COLORS = [
  { name: "INVICT Red", hex: "#BF3437", rgb: "191, 52, 55", role: "Color principal · acentos, alertas, llamadas a la acción", textOn: "#FFFBF4" },
  { name: "Black", hex: "#0A0A0A", rgb: "10, 10, 10", role: "Estructura, fondos oscuros, tipografía principal", textOn: "#FFFBF4" },
  { name: "Bone White", hex: "#FFFBF4", rgb: "255, 251, 244", role: "Fondo cálido, tipografía sobre fondos oscuros", textOn: "#0A0A0A" },
];

const TYPO = [
  { family: "Montserrat", weight: "900", role: "H1 · Títulos display", sample: "INVICT" },
  { family: "Montserrat", weight: "800", role: "H2 · Encabezados", sample: "Cada ronda. Demostrable." },
  { family: "Montserrat", weight: "700", role: "Botones · uppercase", sample: "INGRESAR A LA OPERACIÓN" },
  { family: "Montserrat", weight: "500", role: "Cuerpo · UI", sample: "Control total de tu operación de seguridad." },
];

function H({ children }) {
  return (
    <h2 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 14px", letterSpacing: "-0.02em", color: "#0A0A0A", textTransform: "uppercase" }}>
      <span style={{ borderLeft: "4px solid #BF3437", paddingLeft: 10 }}>{children}</span>
    </h2>
  );
}

export default function BrandPage() {
  return (
    <div style={{ maxWidth: 1120, margin: "0 auto", padding: "28px 24px 60px" }} data-testid="brand-page">
      {/* Hero */}
      <div style={{ background: "linear-gradient(135deg, #0A0A0A 0%, #1a0e0f 100%)", color: "#FFFBF4", padding: "44px 36px", borderRadius: 6, position: "relative", overflow: "hidden", marginBottom: 28 }}>
        <div style={{ position: "absolute", right: -180, top: -180, width: 460, height: 460, background: "radial-gradient(circle, rgba(191,52,55,0.22) 0%, rgba(191,52,55,0) 60%)", pointerEvents: "none" }} />
        <div style={{ fontSize: 10, letterSpacing: "0.3em", color: "#BF3437", fontWeight: 800, marginBottom: 14, textTransform: "uppercase" }}>Manual de Marca · 2026</div>
        <Brand size="xl" />
        <p style={{ marginTop: 22, maxWidth: 560, fontSize: 14, lineHeight: 1.6, color: "rgba(255,251,244,0.72)" }}>
          La identidad de INVICT Security Solutions transmite control, profesionalismo y operatividad. Este manual reúne los lineamientos
          esenciales de uso del logotipo, paleta cromática, tipografía y aplicaciones gráficas.
        </p>
        <a
          href="/brand/INVICT-Brand-Book.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
          style={{ marginTop: 22, display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}
          data-testid="brand-download-pdf"
        >
          <Download size={14} /> Descargar Brand Book (PDF)
        </a>
      </div>

      {/* Logo variations */}
      <section style={{ marginBottom: 36 }}>
        <H>Logotipo · Variaciones oficiales</H>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          <div className="card" style={{ background: "#FFFBF4", border: "1px solid var(--invict-bone-3)" }}>
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Brand size="lg" variant="light-bg" />
            </div>
            <div style={{ fontSize: 10, letterSpacing: "0.18em", fontWeight: 800, color: "#BF3437", textTransform: "uppercase" }}>Versión principal</div>
            <div style={{ fontSize: 12, color: "#6B655A", marginTop: 4 }}>Para fondo bone / blanco. Isotipo rojo + tipografía negra.</div>
          </div>
          <div className="card" style={{ background: "#0A0A0A", border: "1px solid #0A0A0A" }}>
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Brand size="lg" variant="dark-bg" />
            </div>
            <div style={{ fontSize: 10, letterSpacing: "0.18em", fontWeight: 800, color: "#BF3437", textTransform: "uppercase" }}>Versión negativa</div>
            <div style={{ fontSize: 12, color: "rgba(255,251,244,0.6)", marginTop: 4 }}>Para fondos oscuros. Isotipo rojo + tipografía bone.</div>
          </div>
          <div className="card" style={{ background: "#FFFBF4", border: "1px solid var(--invict-bone-3)" }}>
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Brand size="lg" variant="mono-black" />
            </div>
            <div style={{ fontSize: 10, letterSpacing: "0.18em", fontWeight: 800, color: "#BF3437", textTransform: "uppercase" }}>Monocromo negro</div>
            <div style={{ fontSize: 12, color: "#6B655A", marginTop: 4 }}>Para impresiones a 1 tinta, faxes y documentos legales.</div>
          </div>
          <div className="card" style={{ background: "#BF3437", border: "1px solid #BF3437" }}>
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Brand size="lg" variant="mono-white" />
            </div>
            <div style={{ fontSize: 10, letterSpacing: "0.18em", fontWeight: 800, color: "#FFFBF4", textTransform: "uppercase" }}>Monocromo blanco</div>
            <div style={{ fontSize: 12, color: "rgba(255,251,244,0.85)", marginTop: 4 }}>Para fondos rojos, fotografía o merchandising.</div>
          </div>
          <div className="card" style={{ background: "#FFFBF4", border: "1px solid var(--invict-bone-3)" }}>
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Brand size="lg" iconOnly />
            </div>
            <div style={{ fontSize: 10, letterSpacing: "0.18em", fontWeight: 800, color: "#BF3437", textTransform: "uppercase" }}>Isotipo</div>
            <div style={{ fontSize: 12, color: "#6B655A", marginTop: 4 }}>Para favicons, avatares y aplicaciones reducidas.</div>
          </div>
          <div className="card" style={{ background: "#0A0A0A" }}>
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <Brand size="lg" iconOnly variant="mono-white" />
            </div>
            <div style={{ fontSize: 10, letterSpacing: "0.18em", fontWeight: 800, color: "#BF3437", textTransform: "uppercase" }}>Isotipo negativo</div>
            <div style={{ fontSize: 12, color: "rgba(255,251,244,0.6)", marginTop: 4 }}>Para fondos oscuros y patrones de marca.</div>
          </div>
        </div>
      </section>

      {/* Color palette */}
      <section style={{ marginBottom: 36 }}>
        <H>Paleta cromática</H>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          {COLORS.map(c => (
            <div key={c.hex} className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ background: c.hex, color: c.textOn, padding: "28px 22px 22px", minHeight: 150 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.18em", fontWeight: 800, textTransform: "uppercase", opacity: 0.85 }}>{c.name}</div>
                <div style={{ fontSize: 30, fontWeight: 900, marginTop: 8, letterSpacing: "-0.02em" }}>{c.hex.toUpperCase()}</div>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", marginTop: 4, opacity: 0.8 }}>RGB {c.rgb}</div>
              </div>
              <div style={{ padding: "12px 18px", fontSize: 12, color: "#6B655A", borderTop: "1px solid var(--invict-bone-3)" }}>{c.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Typography */}
      <section style={{ marginBottom: 36 }}>
        <H>Tipografía</H>
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.18em", fontWeight: 800, color: "#BF3437", textTransform: "uppercase", marginBottom: 4 }}>Familia principal</div>
          <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: "-0.02em" }}>Montserrat</div>
          <div style={{ fontSize: 12, color: "#6B655A", marginTop: 4 }}>
            Sans-serif geométrica con gran legibilidad en pantalla. Se utiliza en todo el sistema operativo y los reportes oficiales.
          </div>
          <div style={{ marginTop: 22, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
            {TYPO.map((t, i) => (
              <div key={i} style={{ borderLeft: "3px solid #BF3437", paddingLeft: 14 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.15em", color: "#6B655A", textTransform: "uppercase", fontWeight: 700 }}>{t.family} · {t.weight} · {t.role}</div>
                <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: parseInt(t.weight), fontSize: 26, color: "#0A0A0A", marginTop: 6, lineHeight: 1.15, letterSpacing: t.weight === "900" ? "-0.02em" : "0" }}>
                  {t.sample}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Do's and don'ts */}
      <section style={{ marginBottom: 36 }}>
        <H>Usos correctos e incorrectos</H>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} className="max-md:!grid-cols-1">
          <div className="card" style={{ borderLeft: "4px solid #16a34a" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", fontWeight: 800, color: "#16a34a", textTransform: "uppercase", marginBottom: 10 }}>Sí — usos correctos</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#0A0A0A", lineHeight: 1.7 }}>
              <li>Usar el logotipo en alguna de sus 4 versiones oficiales.</li>
              <li>Respetar el área de seguridad mínima alrededor del logotipo.</li>
              <li>Aplicar el isotipo aislado en favicons, avatares y patrones.</li>
              <li>Mantener proporción y orientación originales.</li>
              <li>Contrastar siempre con su fondo (rojo sobre claro, bone sobre oscuro).</li>
            </ul>
          </div>
          <div className="card" style={{ borderLeft: "4px solid #BF3437" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", fontWeight: 800, color: "#BF3437", textTransform: "uppercase", marginBottom: 10 }}>No — usos incorrectos</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#0A0A0A", lineHeight: 1.7 }}>
              <li>No deformar, rotar o estirar el logotipo.</li>
              <li>No cambiar los colores del isotipo o la tipografía.</li>
              <li>No agregar sombras, brillos, efectos 3D o degradados.</li>
              <li>No colocar el logotipo sobre fondos de bajo contraste.</li>
              <li>No reemplazar la tipografía oficial por otra similar.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Tone of voice */}
      <section style={{ marginBottom: 36 }}>
        <H>Tono de voz</H>
        <div className="card">
          <div style={{ fontSize: 13, color: "#0A0A0A", lineHeight: 1.7 }}>
            INVICT comunica con <strong>autoridad, claridad y precisión operativa</strong>. Usamos frases cortas, verbos en presente,
            datos concretos y evitamos adornos. Hablamos del servicio como una <em>operación</em>, no como una promesa.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }} className="max-md:!grid-cols-1">
            <div style={{ background: "var(--invict-bone-2)", padding: 14, borderRadius: 4 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.18em", fontWeight: 800, color: "#16a34a", textTransform: "uppercase" }}>Sí</div>
              <div style={{ marginTop: 6, fontWeight: 700 }}>“Cada ronda. Cada novedad. Demostrable.”</div>
            </div>
            <div style={{ background: "var(--invict-bone-2)", padding: 14, borderRadius: 4 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.18em", fontWeight: 800, color: "#BF3437", textTransform: "uppercase" }}>No</div>
              <div style={{ marginTop: 6, fontWeight: 700, color: "#6B655A" }}>“Te ofrecemos el mejor servicio del mercado.”</div>
            </div>
          </div>
        </div>
      </section>

      {/* PDF embed */}
      <section>
        <H>Brand Book completo</H>
        <div className="card" style={{ padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 12, color: "#6B655A", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <FileText size={14} /> INVICT-Brand-Book.pdf
            </div>
            <a href="/brand/INVICT-Brand-Book.pdf" target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="brand-open-pdf">
              Abrir en pestaña
            </a>
          </div>
          <object data="/brand/INVICT-Brand-Book.pdf" type="application/pdf" style={{ width: "100%", height: 720, border: "1px solid var(--invict-bone-3)", borderRadius: 3 }}>
            <p style={{ padding: 18 }}>Tu navegador no puede mostrar el PDF embebido. <a href="/brand/INVICT-Brand-Book.pdf" target="_blank" rel="noopener noreferrer">Descargá el Brand Book aquí.</a></p>
          </object>
        </div>
      </section>
    </div>
  );
}
